// admin/functions/generateQuizDraftAI.js
// AI quiz-draft generation, calling OUR backend proxy (/generateAiQuestions)
// instead of the external AI service directly — the backend keeps the AI
// URL + token server-side. This module owns prompt construction, response
// parsing/recovery, batching, and top-up retries; the backend is a thin pass
// through.
//
// Output is always an EDITABLE DRAFT — never auto-published. The caller
// shows the draft for review/edit before saving.

const FIXED_OPTIONS = { truefalse: ["True", "False"], mythfact: ["Myth", "Fact"] };

// A single AI call is capped at this many questions; larger requests are
// split into sequential batches so the model stays reliable.
const AI_BATCH_SIZE = 10;
// Consecutive unproductive attempts (network/parse error OR zero usable
// questions returned) allowed before giving up on a batch.
const MAX_EMPTY_RETRIES = 3;

// ---- Prompt construction --------------------------------------------------

function formatInstruction(format) {
  if (format === "truefalse") {
    return (
      `Each item is a True/False question. Options are exactly ["True","False"]. ` +
      `answer_index is 1 for True or 2 for False.`
    );
  }
  if (format === "mythfact") {
    return (
      `Each item is a Myth/Fact statement. Options are exactly ["Myth","Fact"]. ` +
      `answer_index is 1 for Myth or 2 for Fact.`
    );
  }
  return (
    `Each item is a multiple-choice question with exactly 4 distinct options, each under 150 characters. ` +
    `answer_index is the 1-based integer index (1-4) of the correct option.`
  );
}

/**
 * Build the strict generation prompt: JSON-only, fixed schema, exclude list
 * so the AI doesn't repeat questions already generated/exist in this batch.
 */
function buildPrompt({ topic, count, format, excludeTexts }) {
  const exclude =
    excludeTexts && excludeTexts.length
      ? `\n\nEXCLUDE LIST — do not repeat any of these questions (reword or pick entirely different ones):\n` +
        excludeTexts.map((t) => `- ${t}`).join("\n")
      : "";

  return (
    `You are a JSON generator. Respond with ONLY a valid JSON array — no markdown, no code fences, no explanations, no extra text. ` +
    `Generate exactly ${count} quiz question objects about "${topic}". ` +
    `${formatInstruction(format)} ` +
    `Each object must have exactly these fields: question_text (string, under 300 characters), options (array of strings), answer_index (integer). ` +
    `Vary the difficulty and phrasing across questions. Do not duplicate any question within this batch.` +
    `${exclude}\n\n` +
    `Respond with only the JSON array. Example shape:\n` +
    `[{"question_text":"...","options":["..."],"answer_index":1}]`
  );
}

// ---- Response parsing + lenient recovery ---------------------------------

function stripFences(raw) {
  return String(raw || "")
    .replace(/```json/gi, "")
    .replace(/```/g, "")
    .replace(/\\_/g, "_")
    .trim();
}

// Scan char-by-char for balanced {...} blocks so one malformed object
// doesn't take down the whole batch — keep only the ones that actually parse.
function extractJsonObjects(text) {
  const objects = [];
  let depth = 0;
  let start = -1;
  let inString = false;
  let escapeNext = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];

    if (escapeNext) {
      escapeNext = false;
      continue;
    }
    if (ch === "\\") {
      escapeNext = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;

    if (ch === "{") {
      if (depth === 0) start = i;
      depth += 1;
    } else if (ch === "}") {
      depth -= 1;
      if (depth === 0 && start !== -1) {
        const candidate = text.slice(start, i + 1);
        try {
          objects.push(JSON.parse(candidate));
        } catch (e) {
          // skip malformed object, keep scanning
        }
        start = -1;
      }
    }
  }
  return objects;
}

/**
 * Parse the raw AI text into an array of question objects.
 * Fast path: clean [...] array -> JSON.parse directly.
 * Lenient path: malformed array -> recover individual {...} objects.
 */
function parseAiResponse(raw) {
  const cleaned = stripFences(raw);

  if (cleaned.startsWith("[") && cleaned.endsWith("]")) {
    try {
      return JSON.parse(cleaned);
    } catch (e) {
      // fall through to lenient recovery
    }
  }

  const arrayMatch = cleaned.match(/\[[\s\S]*\]/);
  if (arrayMatch) {
    try {
      return JSON.parse(arrayMatch[0]);
    } catch (e) {
      // fall through to lenient recovery
    }
  }

  const recovered = extractJsonObjects(cleaned);
  if (recovered.length) return recovered;

  throw new Error("AI did not return a parseable JSON array");
}

// ---- Normalization + validation -------------------------------------------

function normalizeQuestion(raw, format) {
  const options =
    format === "mcq"
      ? (raw.options || []).map((o) => String(o || "").trim()).filter(Boolean).slice(0, 4)
      : FIXED_OPTIONS[format];

  const questionText = String(raw.question_text || "").trim();
  let answerIndex = parseInt(raw.answer_index, 10);

  if (!questionText) return null;
  if (format === "mcq" && options.length < 2) return null;
  if (Number.isNaN(answerIndex) || answerIndex < 1 || answerIndex > options.length) {
    answerIndex = 1;
  }

  return { question_text: questionText, format, options, answer_index: answerIndex };
}

// ---- Backend call (thin proxy) --------------------------------------------

async function callAiProxy(prompt, token) {
  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/generateAiQuestions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ prompt }),
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok || json.success === false) {
    throw new Error(json.message || `AI proxy request failed (${response.status})`);
  }
  return json.response;
}

// ---- One batch (<= AI_BATCH_SIZE questions) --------------------------------

async function fetchAiBatch({ topic, count, format, excludeTexts, token }) {
  const prompt = buildPrompt({ topic, count, format, excludeTexts });
  const raw = await callAiProxy(prompt, token);
  const parsed = parseAiResponse(raw);

  const excludeSet = new Set(excludeTexts.map((t) => t.toLowerCase()));
  const seen = new Set();
  const results = [];

  for (const item of parsed) {
    const q = normalizeQuestion(item, format);
    if (!q) continue;
    const key = q.question_text.toLowerCase();
    if (excludeSet.has(key) || seen.has(key)) continue;
    seen.add(key);
    results.push(q);
  }

  return results;
}

// ---- Top-up retry loop across multiple batches -----------------------------

/**
 * Generate `count` draft questions, splitting into batches of at most
 * AI_BATCH_SIZE and retrying short/failed batches (top-up) until either the
 * full count is reached or MAX_EMPTY_RETRIES consecutive unproductive
 * attempts occur.
 *
 * @param {object} args
 * @param {string} args.topic
 * @param {number} args.count
 * @param {('mcq'|'truefalse'|'mythfact')} args.format
 * @param {string[]} [args.existingTexts] question texts already in the session (exclude list seed)
 * @param {string} args.token admin bearer token for our backend
 * @returns {Promise<Array>} normalized draft questions
 */
export async function fetchAiQuestions({ topic, count, format = "mcq", existingTexts = [], token }) {
  const collected = [];
  const excludeTexts = [...existingTexts];
  let remaining = count;
  let emptyStreak = 0;

  while (remaining > 0) {
    const batchCount = Math.min(AI_BATCH_SIZE, remaining);

    let batchResults;
    try {
      batchResults = await fetchAiBatch({
        topic,
        count: batchCount,
        format,
        excludeTexts,
        token,
      });
    } catch (err) {
      emptyStreak += 1;
      if (emptyStreak >= MAX_EMPTY_RETRIES) {
        if (collected.length) return collected; // partial success, surface what we have
        throw err; // nothing gathered at all — bubble up
      }
      continue; // retry same remaining count
    }

    if (batchResults.length === 0) {
      emptyStreak += 1;
      if (emptyStreak >= MAX_EMPTY_RETRIES) break;
      continue; // top-up retry for the same shortfall
    }

    emptyStreak = 0; // progress resets the failure counter
    collected.push(...batchResults);
    excludeTexts.push(...batchResults.map((q) => q.question_text));
    remaining = count - collected.length;
  }

  return collected.slice(0, count);
}

// Back-compat default export matching the old single-call signature, now
// routed through the batching/retry pipeline above.
export async function generateQuizDraftAI({ topic, count = 5, format = "mcq", existingTexts = [], token }) {
  return fetchAiQuestions({ topic, count, format, existingTexts, token });
}

export default generateQuizDraftAI;
